/* Groups the UI only. Exact Shopify values still drive server-side filtering and pagination. */
(() => {
  'use strict';
  // Non-English storefronts retain native translated filters instead of guessing translated aliases.
  if (document.documentElement?.lang && !/^en(?:-|$)/i.test(document.documentElement.lang)) return;
  const normalize = value => String(value ?? '').normalize('NFKC').toLowerCase().trim().replace(/[\u2010\u2011\u2013\u2014]/g, '-').replace(/\s+/g, ' ');
  const unique = values => [...new Set(values)];
  const definitions = {
    size: [
      ['xxs', 'XX-Small'], ['xs', 'X-Small'], ['s', 'Small'], ['m', 'Medium'], ['l', 'Large'], ['xl', 'X-Large'],
      ['2xl', '2X-Large'], ['3xl', '3X-Large'], ['4xl', '4X-Large'], ['5xl', '5X-Large'], ['6xl', '6X-Large'],
      ['1x', '1X (Curvy)'], ['2x', '2X (Curvy)'], ['3x', '3X (Curvy)'], ['4x', '4X (Curvy)'], ['5x', '5X (Curvy)'],
      ['curvy', 'Curvy / Plus'], ['one', 'One Size']
    ],
    type: [
      ['sets', 'Matching Sets'], ['dancewear', 'Dancewear'], ['clubwear', 'Clubwear'], ['lingerie', 'Lingerie'], ['bodysuits', 'Bodysuits & Teddies'], ['tops', 'Tops'],
      ['bottoms', 'Bottoms & Skirts'], ['dresses', 'Dresses'], ['rompers', 'Rompers & Jumpsuits'], ['corsets', 'Corsets & Bustiers'],
      ['robes', 'Robes'], ['hosiery', 'Hosiery & Stockings'], ['costumes', 'Costumes'], ['swimwear', 'Swimwear'],
      ['shoes', 'Heels & Shoes'], ['accessories', 'Accessories'], ['other', 'Other Styles']
    ],
    color: [
      ['black', 'Black', '#181818'], ['white', 'White & Ivory', '#faf7ef'], ['red', 'Red', '#b42135'],
      ['pink', 'Pink', '#f15a9c'], ['purple', 'Purple', '#8252ae'], ['blue', 'Blue', '#3166b5'], ['green', 'Green', '#298361'],
      ['yellow', 'Yellow', '#eed84b'], ['orange', 'Orange', '#ef8741'], ['neutral', 'Brown & Nude', '#bc9677'],
      ['silver', 'Gray & Silver', '#b2b5ba'], ['gold', 'Gold', '#d2b264'], ['multi', 'Multicolor & Prints', 'multi'],
      ['clear', 'Clear', 'clear'], ['other', 'Other Shades', 'other']
    ]
  };
  const sizeAtoms = new Map([
    ['xxs','xxs'], ['2xs','xxs'], ['xxsmall','xxs'], ['2xsmall','xxs'], ['extraextrasmall','xxs'],
    ['xs','xs'], ['xsmall','xs'], ['extrasmall','xs'], ['s','s'], ['small','s'], ['m','m'], ['medium','m'],
    ['l','l'], ['large','l'], ['xl','xl'], ['1xl','xl'], ['xlarge','xl'], ['extralarge','xl'],
    ['xxl','2xl'], ['2xl','2xl'], ['xxlarge','2xl'], ['2xlarge','2xl'], ['extraextralarge','2xl'],
    ['xxxl','3xl'], ['3xl','3xl'], ['xxxlarge','3xl'], ['3xlarge','3xl'],
    ['xxxxl','4xl'], ['4xl','4xl'], ['4xlarge','4xl'], ['5xl','5xl'], ['6xl','6xl'],
    ['1x','1x'], ['2x','2x'], ['3x','3x'], ['4x','4x'], ['5x','5x']
  ]);
  function sizes(value) {
    let text = normalize(value).replace(/^(?:size|sizes)\s*[:=-]?\s*/, '');
    const compact = text.replace(/[\s-]/g, '');
    if (/^(?:one(?:size)?(?:fits(?:all|most))?|os|o\/s|osfa|osfm|onesizefitmost)$/.test(compact)) return ['one'];
    if (/^(?:curvy(?:size)?|queen(?:size)?|plus(?:size)?|osqueen|onesizequeen|osplus|onesizeplus)$/.test(compact)) return ['curvy'];
    if (sizeAtoms.has(compact)) {const key=sizeAtoms.get(compact);return /^[1-5]x$/.test(key)?[key,'curvy']:[key];}
    // A slash, connector, or size-code range explicitly includes each endpoint. No intermediate sizes are inferred.
    let parts = text.split(/\s*(?:\/|\\|&|\+|\bto\b|\band\b)\s*/);
    if (parts.length === 1 && /^(?:xxs|xs|s|m|l|xl|[1-6]xl?|small|medium|large)\s*-\s*(?:xxs|xs|s|m|l|xl|[1-6]xl?|small|medium|large)$/.test(text)) parts = text.split(/\s*-\s*/);
    if (parts.length > 1) {
      const groups = parts.map(part => sizeAtoms.get(part.replace(/[\s-]/g,'')));
      if (groups.every(Boolean)) return unique(groups.some(key=>/^[1-5]x$/.test(key))?[...groups,'curvy']:groups);
      if (parts.every(part=>/^\d+(?:\.\d+)?$/.test(part.trim()))) return unique(parts.map(part=>'raw:'+part.trim()));
    }
    // Unknown labels, cup sizes, shoe sizes and numerical sizes stay explicit rather than being guessed.
    return ['raw:' + normalize(value)];
  }
  function types(value) {
    const text = normalize(value);
    if (['roma costume','roma','playboy','elegant moments','dreamgirl','leg avenue','rene rofe','mapale','be wicked','bewicked','music legs','pink lipstick','g-world','fantasy lingerie','penthouse lingerie','oh la la cheri'].includes(text)) return ['other'];
    const groups = [];
    for (const part of text.split(/\s*[,;|]\s*/)) {
      if (/\b(?:sets?|outfits?|dancer sets?)\b/.test(part)) groups.push('sets');
      else if (/\b(?:dancewear|dancer clothing)\b/.test(part)) groups.push('dancewear');
      else if (/\bclubwear\b/.test(part)) groups.push('clubwear');
      else if (/\b(?:bodysuits?|body suits?|tedd(?:y|ies))\b/.test(part)) groups.push('bodysuits');
      else if (/\b(?:corsets?|bustiers?)\b/.test(part)) groups.push('corsets');
      else if (/\b(?:dresses|dress|gowns?)\b/.test(part)) groups.push('dresses');
      else if (/\b(?:rompers?|jumpsuits?|playsuits?|catsuits?)\b/.test(part)) groups.push('rompers');
      else if (/\b(?:swimwear|swimsuits?|bikinis?|beachwear|bathing suits?)\b/.test(part)) groups.push('swimwear');
      else if (/\b(?:hosiery|stockings?|pantyhose|tights?|socks?|bodystockings?|body stockings?)\b/.test(part)) groups.push('hosiery');
      else if (/\b(?:costumes?|cosplay)\b/.test(part)) groups.push('costumes');
      else if (/\b(?:robes?|kimonos?)\b/.test(part)) groups.push('robes');
      else if (/\b(?:tops?|bras?|bralettes?|camisoles?|camis?|halter)\b/.test(part)) groups.push('tops');
      else if (/\b(?:bottoms?|pant(?:y|ies)|thongs?|g[ -]?strings?|shorts?|skirts?|leggings?)\b/.test(part)) groups.push('bottoms');
      else if (/\b(?:shoes?|heels?|boots?|sandals?|pumps?|footwear)\b/.test(part)) groups.push('shoes');
      else if (/\b(?:accessor(?:y|ies)|gloves?|wigs?|pasties|harness(?:es)?|belts?|jewelry|jewellery|poles?)\b/.test(part)) groups.push('accessories');
      else if (/\b(?:lingerie|babydolls?|chemises?|slips?|sleepwear|nightwear)\b/.test(part)) groups.push('lingerie');
      else groups.push('other');
    }
    return unique(groups);
  }
  function colors(value) {
    const text = normalize(value).replace(/rose[ -]?gold/g,'rosegold').replace(/\b(red|black|white)(black|white|red)\b/g,'$1 $2');
    const groups = [];
    const rules = [
      ['black', /\b(?:black|ebony|onyx|blackliant)\b/], ['white', /\b(?:white|ivory|cream|off white|pearl)\b/],
      ['red', /\b(?:red|ruby|burgundy|wine|merlot|maroon|scarlet|garnet|sangria|cherry|cranberry)\b/],
      ['pink', /\b(?:pink|fuchsia|fuschia|magenta|blush|rose|raspberry|berry|amaranth|coral)\b/],
      ['purple', /\b(?:purple|lavender|lilac|violet|plum|grape|eggplant|mauve)\b/],
      ['blue', /\b(?:blue|navy|denim|aqua|turquoise|teal|ocean|sapphire|cobalt)\b/],
      ['green', /\b(?:green|lime|mint|sage|emerald|olive|grass|chartreuse|teal|turquoise|aqua)\b/],
      ['yellow', /\b(?:yellow|lemon|chartreuse)\b/], ['orange', /\b(?:orange|peach|tangerine|coral)\b/],
      ['neutral', /\b(?:brown|nude|beige|tan|caramel|chocolate|honey|toast|sand|bronze|copper|mocha)\b/],
      ['silver', /\b(?:silver|gray|grey|gunmetal|charcoal|smoke|pewter)\b/], ['gold', /\b(?:gold|rosegold|champagne)\b/],
      ['multi', /\b(?:multi(?:color)?|rainbow|tie[ -]?dye|leopard|zebra|camo|camouflage|animal|snakeskin|floral|flowers?|hearts?|galaxy|electric|aliens?|clouds|plaid|polka|print|laser)\b/],
      ['clear', /\b(?:clear|transparent)\b/]
    ];
    for (const [key, pattern] of rules) if (pattern.test(text)) groups.push(key);
    if (!groups.length && /\b(?:iridescent|hologram|holographic|metallic)\b/.test(text)) groups.push('multi');
    return groups.length ? unique(groups) : ['other'];
  }
  function kindFor(data) {
    if (data.param === 'filter.p.product_type') return 'type';
    const param = normalize(data.param), label = normalize(data.label);
    if (/^filter\.v\.option\.(?:size|sizes|clothing size|apparel size)$/.test(param) || (param.startsWith('filter.v.option.') && /^(?:size|sizes|clothing size|apparel size)$/.test(label))) return 'size';
    if (/^filter\.v\.option\.(?:colou?r|colors|colours)$/.test(param) || (param.startsWith('filter.v.option.') && /^(?:colou?r|colors|colours)$/.test(label))) return 'color';
    return null;
  }
  const mappers = {size:sizes,type:types,color:colors};
  function groupsFor(data) {
    const kind = kindFor(data);
    if (!kind || !data.values?.length) return null;
    const groups = new Map(definitions[kind].map(([key,label,swatch])=>[key,{key,label,swatch,values:[]}]));
    for (const item of data.values) {
      for (const key of mappers[kind](item.label)) {
        if (!groups.has(key)) groups.set(key,{key,label:key.startsWith('raw:')&&/^\d+(?:\.\d+)?$/.test(key.slice(4))?key.slice(4):item.label,values:[]});
        groups.get(key).values.push(item);
      }
    }
    return [...groups.values()].filter(group=>group.values.length).sort((a,b)=>a.key.startsWith('raw:')&&b.key.startsWith('raw:')?a.label.localeCompare(b.label,undefined,{numeric:true}):0);
  }
  const metadataKey = param => 'esf_' + param;
  const sameSet = (a,b) => a.size === b.size && [...a].every(value=>b.has(value));
  function activeState(data, groups, url) {
    // URL values are authoritative, including values not returned in a narrowed facet list.
    const active = new Set(url.searchParams.getAll(data.param).flatMap(value=>data.values.some(item=>item.value===value)?[value]:value.split(',')));
    if (!active.size) for (const item of data.values) if (item.active) active.add(item.value);
    const marker = url.searchParams.get(metadataKey(data.param));
    if (marker !== null) {
      let keys;try {keys=JSON.parse(marker);} catch {keys=marker.split('|');}
      const selected = new Set((Array.isArray(keys)?keys:[]).filter(key=>groups.some(group=>group.key===key)));
      const expanded = new Set(groups.filter(group=>selected.has(group.key)).flatMap(group=>group.values.map(item=>item.value)));
      if (sameSet(active,expanded)) return {managed:true,selected,active};
    }
    if (!active.size) return {managed:true,selected:new Set(),active};
    // Old/bookmarked native URLs retain their exact meaning until a grouped choice is clicked.
    const selected = new Set(groups.filter(group=>group.values.every(item=>active.has(item.value))).map(group=>group.key));
    return {managed:false,selected,active};
  }
  function applyChoice(formData, model, selected) {
    formData.delete(model.data.param);
    formData.delete(metadataKey(model.data.param));
    const values = unique(model.groups.filter(group=>selected.has(group.key)).flatMap(group=>group.values.map(item=>item.value)));
    for (const value of values) formData.append(model.data.param,value);
    if (selected.size) formData.set(metadataKey(model.data.param),JSON.stringify([...selected]));
  }
  // Single-value counts come from Shopify facets. Grouped colors require a union,
  // because one product may have several native colors and must be counted once.
  const colorCountCache = new Map();
  const colorCountQueue = [];
  let activeColorCounts = 0;
  function pumpColorCounts() {
    while (activeColorCounts < 3 && colorCountQueue.length) {
      const {url,resolve,reject} = colorCountQueue.shift(); activeColorCounts++;
      fetch(url,{credentials:'same-origin',signal:AbortSignal.timeout(12000)})
        .then(response=>{if(!response.ok)throw Error('Color count unavailable');return response.text();})
        .then(html=>{
          const text=new DOMParser().parseFromString(html,'text/html').querySelector('[data-es-result-count]')?.textContent.trim();
          if(!/^\d+$/.test(text||''))throw Error('Invalid color count');resolve(Number(text));
        }).catch(reject).finally(()=>{activeColorCounts--;pumpColorCounts();});
    }
  }
  function colorCountURL(data, item) {
    const url=new URL(location.href);
    url.searchParams.delete(data.param);url.searchParams.delete(metadataKey(data.param));
    url.searchParams.delete('page');url.searchParams.delete('sections');url.searchParams.delete('section_id');
    for(const value of unique(item.values.map(value=>value.value)))url.searchParams.append(data.param,value);
    url.searchParams.set('section_id','es-color-count');url.hash='';
    return url.pathname+'?'+url.searchParams.toString();
  }
  function exactColorCount(url) {
    if(!colorCountCache.has(url)){
      const promise=new Promise((resolve,reject)=>{colorCountQueue.push({url,resolve,reject});pumpColorCounts();});
      colorCountCache.set(url,promise);promise.catch(()=>colorCountCache.delete(url));
    }
    return colorCountCache.get(url);
  }
  function loadColorCounts(model) {
    for(const item of model.groups) {
      if(!item.countNode || item.values.length<2 || item.countState==='loading' || item.countState==='ready')continue;
      item.countState='loading';item.countNode.textContent='(…)';
      exactColorCount(colorCountURL(model.data,item)).then(count=>{
        item.countState='ready';item.countNode.textContent='('+count+')';
        item.countNode.removeAttribute('title');
      }).catch(()=>{
        item.countState='error';item.countNode.textContent='';
        item.countNode.title='Count temporarily unavailable';
      });
    }
  }
  const models = new WeakMap();
  function renderGroup(group) {
    const dataNode = group.querySelector('[data-es-facet-data]');
    if (!dataNode || models.has(group)) return;
    let data;
    try {data=JSON.parse(dataNode.textContent);} catch {return;}
    const groups = groupsFor(data);
    const container = group.querySelector('.collection__filter-collapsible');
    if (!groups || !container) return;
    const kind = kindFor(data), state = activeState(data,groups,new URL(location.href));
    const list = document.createElement('div');
    list.className='es-facet-choices es-facet-choices--'+kind;
    const model={data,groups,kind,state,list,group};
    groups.forEach((item,index)=>{
      const label=document.createElement('label');label.className='es-facet-choice';
      const input=document.createElement('input');input.type='checkbox';input.name=data.param;input.value=item.key;input.dataset.esFilterChoice='';
      input.checked=state.selected.has(item.key);
      input.indeterminate=!state.managed&&!input.checked&&item.values.some(value=>state.active.has(value.value));
      input.disabled=!input.checked&&!input.indeterminate&&item.values.every(value=>value.count===0);
      const idPrefix=group.closest('form')?.id||'filters';input.id=idPrefix+'-es-'+kind+'-'+index;
      const body=document.createElement('span');body.className='es-facet-choice__body';
      if(kind==='color'){
        const swatch=document.createElement('span');swatch.className='es-facet-choice__swatch';swatch.setAttribute('aria-hidden','true');
        if(item.swatch?.startsWith('#'))swatch.style.backgroundColor=item.swatch;
        else swatch.dataset.effect=item.swatch||'other';body.append(swatch);
      }
      const text=document.createElement('span');text.textContent=item.label;body.append(text);
      if(kind==='color'){
        const count=document.createElement('span');count.className='es-facet-choice__count';item.countNode=count;
        if(item.values.length===1){count.textContent='('+item.values[0].count+')';item.countState='ready';}
        else if(item.values.every(value=>value.count===0)){count.textContent='(0)';item.countState='ready';}
        else count.textContent='(…)';
        body.append(count);
      }
      label.append(input,body);list.append(label);
      input.addEventListener('change',()=>{model.state.managed=true;model.state.selected=new Set([...list.querySelectorAll('input:checked')].map(el=>el.value));});
    });
    // Native controls remain available if JavaScript is disabled; disable them only after building the replacement.
    for(const child of [...container.children]){
      child.hidden=true;child.dataset.esNativeFacet='';
      child.querySelectorAll('input,select').forEach(input=>{input.disabled=true;});
    }
    container.append(list);group.dataset.esGrouped=kind;models.set(group,model);
    const title=group.querySelector('.collection__filter-group-name');
    if(kind==='color'){
      title?.addEventListener('click',()=>loadColorCounts(model));
      if(title?.getAttribute('aria-expanded')==='true')loadColorCounts(model);
    }
    if(kind==='type'&&title){const textNode=[...title.childNodes].find(node=>node.nodeType===Node.TEXT_NODE&&node.textContent.trim());if(textNode)textNode.textContent='Style ';else {const span=title.querySelector('span');if(span)span.textContent='Style';}}
  }
  function formData(form) {
    const result=new FormData(form);
    for(const group of form.querySelectorAll('[data-es-grouped]')){
      const model=models.get(group);if(!model)continue;
      if(model.state.managed)applyChoice(result,model,new Set([...model.list.querySelectorAll('input:checked')].map(el=>el.value)));
      else {
        result.delete(model.data.param);result.delete(metadataKey(model.data.param));
        for(const value of model.state.active)result.append(model.data.param,value);
      }
    }
    return result;
  }
  function removalURL(model, key) {
    const url=new URL(location.href), data=new FormData();
    for(const [name,value]of url.searchParams)data.append(name,value);
    const selected=new Set(model.state.selected);selected.delete(key);applyChoice(data,model,selected);
    url.search=new URLSearchParams(data).toString();url.searchParams.delete('page');url.searchParams.delete('section_id');return url.pathname+url.search;
  }
  function activePills(section) {
    const byParam=new Map();
    section.querySelectorAll('[data-es-grouped]').forEach(group=>{const model=models.get(group);if(model)byParam.set(model.data.param,model);});
    for(const recap of section.querySelectorAll('.collection__active-filters,.collection__mobile-active-filters')){
      recap.querySelectorAll('[data-es-group-pill]').forEach(el=>el.remove());
      for(const model of byParam.values()){
        if(!model.state.managed)continue;
        recap.querySelectorAll('label[for],button[data-es-native-pill]').forEach(el=>{
          const target=el.getAttribute('for')||el.dataset.esNativePill;
          if(target.startsWith(model.data.param+'-')||target.startsWith('mobile-'+model.data.param+'-'))el.hidden=true;
        });
        // Mobile native pills have only data-url, so use explicit Liquid metadata to identify their facet.
        recap.querySelectorAll('[data-es-native-param]').forEach(el=>{if(el.dataset.esNativeParam===model.data.param)el.hidden=true;});
        for(const group of model.groups.filter(item=>model.state.selected.has(item.key))){
          const button=document.createElement('button');button.type='button';button.className='es-active-filter';button.dataset.esGroupPill='';button.dataset.action='clear-filters';button.dataset.url=removalURL(model,group.key);
          button.textContent=(model.kind==='type'?'Style':model.data.label)+': '+group.label;
          const cross=document.createElement('span');cross.textContent=' ×';cross.setAttribute('aria-hidden','true');button.append(cross);recap.prepend(button);
        }
      }
    }
    let count=Number(section.dataset.esActiveCount||0);
    for(const model of byParam.values())if(model.state.managed)count+=model.state.selected.size-model.data.values.filter(item=>item.active).length;
    count=Math.max(0,count);
    section.querySelectorAll('[data-es-selected-count],.collection-drawer__filter-count').forEach(el=>{el.textContent=count?'('+count+')':'';el.hidden=!count;el.style.display=count?'':'none';});
    section.querySelectorAll('.collection__mobile-active-clear').forEach(el=>{el.hidden=count<2;});
  }
  function refresh(root=document) {
    root.querySelectorAll('.collection__filter-group').forEach(renderGroup);
    const sections=root.matches?.('[data-section-type="collection"]')?[root]:root.querySelectorAll('[data-section-type="collection"]');
    for(const section of sections)activePills(section);
  }
  // A tiny public bridge lets Warehouse keep its original AJAX, sort, pagination, and history handling.
  window.ESFilters={refresh,formData,sizes,types,colors,groupsFor,activeState,applyChoice};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>refresh(),{once:true});else refresh();
  document.addEventListener('shopify:section:load',event=>refresh(event.target));
})();
